import { ReactElement, useCallback, useEffect, useState } from "react";
import { IonContent, IonPage, IonSpinner } from "@ionic/react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import useQuery from "../../hooks/useQuery";
import { useConfig } from "../../contexts/ConfigContext";
import {
  getApiQuestionnairePreview,
  getPreviewDependencies,
} from "../../services/questionnaire.service";
import { defaultAnswerValueByType } from "../../helper/answer";
import Questionnaire from "../../components/organisms/SequenceDetails/Questionnaire";
import {
  QuestionnaireDetailsType,
  QuestionnaireSession,
} from "../../types/Questionnaire";
import { QuestionType } from "../../types/Question";
import { AnswerType } from "../../types/Answer";
import { Dependency } from "../../types/Dependency";

/**
 * No-login questionnaire flow simulator. Opened from the Diapason "Simulate" button via a
 * signed ?token=; renders the REAL participant flow (the Questionnaire organism + canonical
 * engine) so researchers can verify dependencies, follow-up sets and end conditions without
 * creating an account or a sequence. Nothing is persisted: onSubmitAnswer is a no-op, and
 * completion shows a local panel instead of advancing a sequence. Per-account behaviour
 * (which questionnaires are offered, randomisation arm, scheduling, resume) is out of scope.
 */

// "Study default" (empty) lets the API pick the study's primary language; the rest let a
// researcher preview each translation. Unconfigured languages surface as a load error.
const PREVIEW_LANGS: { code: string; labelKey: string }[] = [
  { code: "", labelKey: "questionnaire_preview_lang_default" },
  { code: "fr", labelKey: "questionnaire_preview_lang_fr" },
  { code: "en", labelKey: "questionnaire_preview_lang_en" },
  { code: "es", labelKey: "questionnaire_preview_lang_es" },
  { code: "ar", labelKey: "questionnaire_preview_lang_ar" },
];

// Mirror of SequenceQuestionnaire's session build, with NO saved answers: every question
// starts at its default value and the flow begins at the first top-level question.
const buildPreviewSession = (
  data: QuestionnaireDetailsType,
  dependencies: Dependency[]
): QuestionnaireSession => {
  const { questions, version } = data;
  const answers: AnswerType[] = questions.map((q) => ({
    value: defaultAnswerValueByType[q.type],
    questionId: q.id,
    questionType: q.type,
  }));
  const topLevel = questions.filter((q) => q.set_driver_question_id == null);
  const hasSets = topLevel.length !== questions.length;
  const history: QuestionType[] = hasSets ? topLevel.slice(0, 1) : questions.slice(0, 1);
  return {
    is_scored: data.is_scored ?? false,
    questions,
    answers,
    history,
    version,
    dependencies,
    sections: data.sections ?? [],
  };
};

// Single-question preview (per-row "Simulate this question"). Render exactly one question in
// isolation: strip its set/section wiring so it shows as a plain standalone question with its
// own input + validation, and completes on "Next" (getNextQuestion returns null -> done panel).
const buildSingleQuestionSession = (
  data: QuestionnaireDetailsType,
  questionId: number
): QuestionnaireSession | null => {
  const found = data.questions.find((q) => Number(q.id) === questionId);
  if (!found) return null;
  const q: QuestionType = {
    ...found,
    set_driver_question_id: null,
    set_trigger_answer_id: null,
    set_order: null,
    section_id: null,
  };
  return {
    is_scored: data.is_scored ?? false,
    questions: [q],
    answers: [
      { value: defaultAnswerValueByType[q.type], questionId: q.id, questionType: q.type },
    ],
    history: [q],
    version: data.version,
    dependencies: [],
    sections: [],
  };
};

const QuestionnairePreview = (): ReactElement => {
  const { t, i18n } = useTranslation();
  const query = useQuery();
  const token = query.get("token") ?? "";
  const { questionnaireId } = useParams<{ questionnaireId: string }>();
  const { study } = useConfig();
  // The study id the token was signed for is passed explicitly by Diapason. In single-study
  // mode Resonance's config is pinned to ENV.STUDY.id and ignores the URL slug, so trusting
  // config.study.id would 401 any study whose id differs from the pinned one. Fall back to
  // config only if the param is absent. (Tampering is caught by the token's HMAC on the API.)
  const queryStudyId = Number(query.get("studyId"));
  const studyId =
    Number.isFinite(queryStudyId) && queryStudyId > 0 ? queryStudyId : study?.id;
  // Optional: preview a SINGLE question (per-row Simulate) instead of the whole flow.
  const queryQuestionId = Number(query.get("questionId"));
  const singleQuestionId =
    Number.isFinite(queryQuestionId) && queryQuestionId > 0 ? queryQuestionId : null;

  const [lang, setLang] = useState<string>("");
  const [session, setSession] = useState<QuestionnaireSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [error, setError] = useState(false);
  const [done, setDone] = useState(false);
  const [score, setScore] = useState<number | null>(null);

  useEffect(() => {
    let active = true;
    const load = async (): Promise<void> => {
      if (!token || !questionnaireId || !studyId) {
        setInvalid(true);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(false);
        setDone(false);
        const [{ data }, { data: dependencies }] = await Promise.all([
          getApiQuestionnairePreview(studyId, Number(questionnaireId), lang, token),
          getPreviewDependencies(studyId, Number(questionnaireId), token),
        ]);
        if (!active) return;
        setSession(
          singleQuestionId
            ? buildSingleQuestionSession(data, singleQuestionId)
            : buildPreviewSession(data, dependencies ?? [])
        );
      } catch (e) {
        if (!active) return;
        // 401 = bad/expired token; everything else (incl. an unconfigured language) is a
        // generic load error.
        if (e?.response?.status === 401) setInvalid(true);
        else setError(true);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [token, questionnaireId, studyId, lang, singleQuestionId]);

  // Switch the whole preview UI (buttons + locale-aware repetition ordinals) to the chosen
  // content language, so headings/labels don't mix the researcher's UI language with the
  // previewed translation. Empty ('' = study default) leaves the UI language as-is.
  const onLangChange = useCallback(
    (code: string): void => {
      setLang(code);
      if (code) i18n.changeLanguage(code);
    },
    [i18n]
  );

  const onQuestionnaireDone = useCallback((total: number | null): void => {
    setScore(total);
    setDone(true);
  }, []);

  const restart = useCallback((): void => {
    setDone(false);
    setScore(null);
    // Rebuild a fresh session from the current questions/dependencies (start over).
    setSession((prev) =>
      prev
        ? buildPreviewSession(
            { questions: prev.questions, version: prev.version, is_scored: prev.is_scored, sections: prev.sections } as QuestionnaireDetailsType,
            prev.dependencies
          )
        : prev
    );
  }, []);

  const body = ((): ReactElement => {
    if (loading) {
      return (
        <Centered>
          <IonSpinner name="crescent" />
          <Muted>{t("questionnaire_preview_loading")}</Muted>
        </Centered>
      );
    }
    if (invalid) {
      return (
        <Centered>
          <Title>{t("questionnaire_preview_title")}</Title>
          <Muted>{t("questionnaire_preview_invalid")}</Muted>
        </Centered>
      );
    }
    if (error) {
      return (
        <Centered>
          <Title>{t("questionnaire_preview_title")}</Title>
          <Muted>{t("questionnaire_preview_error")}</Muted>
        </Centered>
      );
    }
    if (!session || session.questions.length === 0) {
      return (
        <Centered>
          <Title>{t("questionnaire_preview_title")}</Title>
          <Muted>{t("questionnaire_preview_empty")}</Muted>
        </Centered>
      );
    }
    if (done) {
      return (
        <Centered>
          <Title>{t("questionnaire_preview_done_title")}</Title>
          {session.is_scored && score != null ? (
            <Muted>{t("questionnaire_preview_done_score", { score })}</Muted>
          ) : null}
          <RestartButton onClick={restart}>
            {t("questionnaire_preview_restart")}
          </RestartButton>
        </Centered>
      );
    }
    return (
      <Questionnaire
        questionnaireTitle={t("questionnaire_preview_title")}
        session={session}
        onSessionChange={setSession}
        onSubmitAnswer={async () => {}}
        onQuestionnaireDone={onQuestionnaireDone}
        onNextClick={() => {}}
        onPrevClick={() => {}}
      />
    );
  })();

  return (
    <IonPage>
      <IonContent>
        <Banner>
          <BannerText>{t("questionnaire_preview_banner")}</BannerText>
          <Controls>
            <LangSelect
              value={lang}
              onChange={(e) => onLangChange(e.target.value)}
              aria-label={t("questionnaire_preview_lang_default")}
            >
              {PREVIEW_LANGS.map((l) => (
                <option key={l.code || "default"} value={l.code}>
                  {t(l.labelKey)}
                </option>
              ))}
            </LangSelect>
            <RestartLink type="button" onClick={restart}>
              {t("questionnaire_preview_restart")}
            </RestartLink>
          </Controls>
        </Banner>
        {/* Resonance is a mobile-first Ionic app; on a desktop browser its full-viewport
            layout stretches edge-to-edge and pushes the Next/Prev buttons far below the fold.
            Constrain the preview to a centered phone-sized frame so the card + buttons read
            like the real device (preview-only — the shared mobile components are untouched). */}
        <Stage>
          <PhoneFrame>{body}</PhoneFrame>
        </Stage>
      </IonContent>
    </IonPage>
  );
};

export default QuestionnairePreview;

const Banner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 16px;
  background: #fff6e5;
  border-bottom: 1px solid #f1d9a8;
  /* Keep the simulation notice + controls pinned while the phone frame scrolls. */
  position: sticky;
  top: 0;
  z-index: 5;
`;

/* Desktop backdrop that centers the phone frame. */
const Stage = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 12px;
  background: #eceef1;
`;

/* Phone-sized frame: bounded width (so it isn't full-width on big screens) and a bounded
   height (so the flow's min-height:100% resolves to the frame, keeping Next/Prev at the
   bottom of the visible screen). Taller content scrolls inside the frame, like a phone. */
const PhoneFrame = styled.div`
  width: 100%;
  max-width: 460px;
  height: calc(100vh - 120px);
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
  overflow-y: auto;
  overflow-x: hidden;
`;

const BannerText = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #8a6d3b;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LangSelect = styled.select`
  font-size: 13px;
  padding: 4px 8px;
  border: 1px solid #d9c08a;
  border-radius: 6px;
  background: #fff;
  color: #6b5424;
`;

const RestartLink = styled.button`
  font-size: 13px;
  font-weight: 600;
  color: #8a6d3b;
  background: transparent;
  border: 1px solid #d9c08a;
  border-radius: 6px;
  padding: 4px 10px;
  cursor: pointer;
`;

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
  padding: 48px 16px;
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: #2c3e50;
`;

const Muted = styled.p`
  font-size: 14px;
  color: rgba(69, 80, 86, 0.75);
  margin: 0;
`;

const RestartButton = styled.button`
  margin-top: 8px;
  font-size: 15px;
  font-weight: 600;
  color: var(--app-color-accent-contrast, #fff);
  background: var(--app-color-accent, #2db6b4);
  border: none;
  border-radius: 8px;
  padding: 10px 24px;
  cursor: pointer;
`;
